import React from "react";
import type { WorkflowCatalogEntry } from "../types/workflow";

export function workflowLabel(ref: string): string {
  const id = ref.split("/")[1] ?? ref;
  return id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface CatalogCardProps {
  entry: WorkflowCatalogEntry;
  onClick: () => void;
  highlighted?: boolean;
  /** Disable launch (dimmed, not clickable). Used for the client-required gate. */
  disabled?: boolean;
  /** Tooltip/aria hint shown when disabled (e.g. "Select a client first"). */
  disabledReason?: string;
}

export const CatalogCard: React.FC<CatalogCardProps> = ({
  entry,
  onClick,
  highlighted,
  disabled,
  disabledReason,
}) => {
  const label = entry.name || workflowLabel(entry.ref);
  const mins = entry.estimated_manual_minutes;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled}
      title={disabled ? disabledReason : undefined}
      data-testid={`catalog-card-${entry.id}`}
      className={
        "w-full rounded-[10px] border border-primary-warning-300 bg-primary-warning-50 px-3 py-[10px] text-left transition-shadow duration-200 " +
        (disabled
          ? "cursor-not-allowed opacity-50 "
          : "hover:bg-primary-warning-100 hover:shadow-sm active:scale-[0.99] ") +
        (highlighted ? "ring-2 ring-sunrise ring-offset-1" : "")
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[6px]">
            <span className="text-sm font-semibold text-primary-gray-light-900">
              {label}
            </span>
            {highlighted && (
              <span
                data-testid={`catalog-card-suggested-label-${entry.id}`}
                className="rounded-full bg-sunrise px-[6px] py-[1px] text-2xs font-bold uppercase tracking-[0.04em] text-primary-gray-light-900"
              >
                Suggested
              </span>
            )}
          </div>
          {entry.description && (
            <div className="mt-[2px] truncate text-xs text-primary-gray-light-500">
              {entry.description}
            </div>
          )}
          <div className="mt-1 text-xs text-primary-gray-light-400">
            {entry.triggers.join(", ")} · {entry.phase_count} phases
            {mins != null && <> · ~{mins} min saved</>}
          </div>
        </div>
        <span className="flex-shrink-0 self-center text-base leading-none text-primary-warning-700">
          ›
        </span>
      </div>
    </button>
  );
};

export default CatalogCard;
